import { satisfies, valid } from 'semver';
import type { Host, Platform } from '#dn-types/host.ts';

/**
 * Oldest client release per platform that the api lets onto an IPv6 overlay; mirrors its `MinIPv6*Version`.
 * A dnclient-desktop host is dnclient running under the desktop app and reports dnclient's own version.
 * nebula-apple ships IPv6-capable from its first build, so the api doesn't gate it.
 */
const MIN_IPV6_VERSION: Partial<Record<Platform, string>> = {
  dnclient: '0.9.3',
  'dnclient-desktop': '0.9.3',
  mobile: '0.10.0',
};

export type IPv6Support = 'supported' | 'outdated' | 'unknown';

/**
 * Whether a host's last-reported client can use the IPv6 address a network upgrade assigns it.
 * A host that has never checked in reports no platform or version, and a local build may report a non-semver
 * version, so neither can be confirmed either way.
 */
export function getIPv6Support({ metadata: { platform, version } }: Pick<Host, 'metadata'>): IPv6Support {
  if (platform === null || version === null) return 'unknown';
  const minVersion = MIN_IPV6_VERSION[platform];
  if (minVersion === undefined) return 'supported';
  // The api treats a `-dev` build as the release it precedes, so `0.9.3-dev` passes a 0.9.3 floor.
  const release = version.replace(/-dev$/, '');
  if (valid(release) === null) return 'unknown';
  // Without includePrerelease, semver ranks a prerelease like `0.9.4-rc1` below every `>=0.9.3` floor.
  return satisfies(release, `>=${minVersion}`, { includePrerelease: true }) ? 'supported' : 'outdated';
}
